import React, { Suspense, lazy, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { AuthProvider } from './contexts/AuthContext';
import { useAuthenticatedEffect } from './hooks/useAuthenticatedEffect';
import { StatusColorsProvider } from './contexts/StatusColorsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConnectionProvider, useConnection } from './contexts/ConnectionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import ConnectingOverlay from './components/ConnectingOverlay';
import OfflineBanner from './components/OfflineBanner';
import { DateProvider } from './contexts/DateContext';
import HomePage from './pages/HomePage';
import BudowyPanel from './components/BudowyPanel';
import ErrorBoundaryDebug from './components/ErrorBoundaryDebug';
import { authenticatedFetch } from './services/authService';
import './app.css';

// Panel administracyjny: pod /admin stoi wersja przepisana na moduły (pages/admin/).
// Poprzedni, jednoplikowy AdminDashboard zostaje pod /admin-stary jako wyjście awaryjne —
// administracja jest jedynym miejscem nadawania uprawnień, więc dopóki nowa wersja nie
// zostanie przeklikana na produkcji, nie odcinamy drogi powrotu. Do usunięcia po potwierdzeniu.
const AdminPanel = lazy(() => import('./pages/admin/AdminPanel'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SurowcePage = lazy(() => import('./pages/SurowcePage'));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage'));

const computeBasename = () => {
  try {
    const p = window.location.pathname || '/';
    if (p.startsWith('/portal')) return '/portal';
    if (p.startsWith('/zamowienia')) return '/zamowienia';
    if (p.startsWith('/kontroling')) return '/kontroling';
    if (p.startsWith('/sprzedaz')) return '/sprzedaz';
    if (p.startsWith('/zarzadzanie')) return '/zarzadzanie';
    if (p.startsWith('/wezel')) return '/wezel';
    return '/';
  } catch {
    return '/';
  }
};

const basename = computeBasename();

const GMAPS_LIBRARIES = ['marker'] as const;
const WEZEL_ICON_PX = 36;
const BUDOWA_ICON_PX = 36;

// Map component for budowy page
const BudowyMap: React.FC<{
  apiKey: string;
  mapId: string;
  center: { lat: number; lng: number };
  wezels: Array<{ id: number; nazwa: string; lat: number; lng: number }>;
  budowy: Array<{ id: number; nazwa: string | null; lat: number; lng: number }>;
  selectedBudowaId: number | null;
  clickedLocation: { lat: number; lng: number } | null;
  onMapLoad: (map: any) => void;
  onWezelClick: (wezel: { id: number; lat: number; lng: number }) => void;
  onBudowaClick: (budowaId: number) => void;
  onMapClick?: (e: any) => void;
  editingMode: boolean;
}> = ({ apiKey, mapId, center, wezels, budowy, selectedBudowaId, clickedLocation, onMapLoad, onWezelClick, onBudowaClick, onMapClick, editingMode }) => {
  const { isLoaded, loadError } = useJsApiLoader({ 
    id: 'google-map-script', 
    googleMapsApiKey: apiKey || '', 
    version: 'weekly', 
    libraries: GMAPS_LIBRARIES as any 
  });
  
  const mapRef = useRef<any | null>(null);
  const wezelMarkersRef = useRef<any[]>([]);
  const budowaMarkersRef = useRef<any[]>([]);
  const selectedBudowaMarkerRef = useRef<any | null>(null);
  const clickedLocationMarkerRef = useRef<any | null>(null);

  useEffect(() => {
    const g: any = (typeof window !== 'undefined') ? (window as any).google : undefined;
    const map = mapRef.current;
    if (!g || !map || !isLoaded) return;

    // Clear existing markers
    wezelMarkersRef.current.forEach(m => { try { m.setMap(null); } catch {} });
    wezelMarkersRef.current = [];
    budowaMarkersRef.current.forEach(m => { try { m.setMap(null); } catch {} });
    budowaMarkersRef.current = [];
    if (selectedBudowaMarkerRef.current) {
      try { selectedBudowaMarkerRef.current.setMap(null); } catch {}
      selectedBudowaMarkerRef.current = null;
    }
    
    // Create clicked location marker (temporary marker showing where user clicked)
    // This marker will be replaced by a permanent budowa marker after save
    if (clickedLocationMarkerRef.current) {
      try { clickedLocationMarkerRef.current.setMap(null); } catch {}
      clickedLocationMarkerRef.current = null;
    }
    if (clickedLocation) {
      const img = document.createElement('img');
      img.src = '/blue_pin_point.png';
      img.style.width = `${BUDOWA_ICON_PX}px`;
      img.style.height = `${BUDOWA_ICON_PX}px`;
      img.style.transform = 'translate(-50%, -100%)';
      // Make temporary marker visually distinct (brighter, with stronger shadow)
      img.style.filter = 'brightness(1.5) drop-shadow(0 0 8px rgba(0,150,255,0.9))';
      img.style.opacity = '0.9';
      const marker = new g.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: clickedLocation.lat, lng: clickedLocation.lng },
        title: 'Nowa lokalizacja budowy (tymczasowa)',
        content: img,
        zIndex: 2000
      });
      clickedLocationMarkerRef.current = marker;
    }

    // Create wezel markers (red)
    for (const w of wezels) {
      const img = document.createElement('img');
      img.src = '/red_pin_point.png';
      img.style.width = `${WEZEL_ICON_PX}px`;
      img.style.height = `${WEZEL_ICON_PX}px`;
      img.style.transform = 'translate(-50%, -100%)';
      const marker = new g.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: w.lat, lng: w.lng },
        title: w.nazwa,
        content: img
      });
      marker.addListener('click', () => {
        onWezelClick(w);
      });
      wezelMarkersRef.current.push(marker);
    }

    // Create budowa markers (blue)
    for (const b of budowy) {
      const isSelected = b.id === selectedBudowaId;
      const img = document.createElement('img');
      img.src = '/blue_pin_point.png';
      img.style.width = `${BUDOWA_ICON_PX}px`;
      img.style.height = `${BUDOWA_ICON_PX}px`;
      img.style.transform = 'translate(-50%, -100%)';
      if (isSelected) {
        img.style.filter = 'brightness(1.2) drop-shadow(0 0 4px rgba(0,0,255,0.5))';
      }
      const marker = new g.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: b.lat, lng: b.lng },
        title: b.nazwa || '(bez nazwy)',
        content: img,
        zIndex: isSelected ? 1000 : 100
      });
      marker.addListener('click', () => {
        onBudowaClick(b.id);
      });
      if (isSelected) {
        selectedBudowaMarkerRef.current = marker;
        map.panTo({ lat: b.lat, lng: b.lng });
      } else {
        budowaMarkersRef.current.push(marker);
      }
    }
  }, [wezels, budowy, selectedBudowaId, clickedLocation, isLoaded, mapId, onWezelClick, onBudowaClick]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full text-error text-sm">
        Błąd ładowania Google Maps
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full text-muted text-sm">
        Ładowanie Google Maps...
      </div>
    );
  }

  // Validate mapId is required
  if (!mapId || !mapId.trim()) {
    return (
      <div className="flex items-center justify-center h-full text-error text-sm">
        Błąd: Map ID jest wymagane dla AdvancedMarkerElement
      </div>
    );
  }

  return (
    <GoogleMap
      onLoad={(map) => {
        mapRef.current = map;
        onMapLoad(map);
      }}
      onUnmount={() => {
        mapRef.current = null;
      }}
      onClick={onMapClick}
      mapContainerStyle={{ height: '100%', width: '100%', minHeight: '300px' }}
      center={center}
      zoom={13}
      options={{ 
        streetViewControl: false, 
        mapTypeControl: false,
        mapId: mapId.trim()
      }}
    />
  );
};

const StandaloneBudowy: React.FC = () => {
  // Helper to read Map ID synchronously (like MapView does)
  const readMapId = (): string => {
    try {
      const meta = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null)?.content || '';
      const fromWin = (window as any)?.GMAPS_MAP_ID || '';
      const fromStorage = (typeof window !== 'undefined') ? localStorage.getItem('GMAPS_MAP_ID') : null;
      return String(meta || fromWin || fromStorage || '');
    } catch { return ''; }
  };
  
  // Helper to read API key synchronously
  const readApiKey = (): string => {
    try {
      const w: any = (typeof window !== 'undefined') ? (window as any) : undefined;
      const meta: HTMLMetaElement | null = (typeof document !== 'undefined') ? (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null) : null;
      const fromWindow = w?.GMAPS_API_KEY;
      const fromMeta = meta?.content;
      const fromStorage = (typeof window !== 'undefined') ? localStorage.getItem('GMAPS_API_KEY') : null;
      return (fromWindow || fromMeta || fromStorage || '').toString();
    } catch { return ''; }
  };
  
  const [apiKey, setApiKey] = useState<string>(() => readApiKey());
  const [mapId, setMapId] = useState<string>(() => readMapId());
  const [panelOpen, setPanelOpen] = useState<boolean>(true);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [regions, setRegions] = useState<Array<{ id: number; nazwa: string }>>([]);
  const [wezels, setWezels] = useState<Array<{ id: number; nazwa: string; lat: number; lng: number }>>([]);
  const [budowy, setBudowy] = useState<Array<{ id: number; nazwa: string | null; lat: number; lng: number }>>([]);
  const [selectedBudowaId, setSelectedBudowaId] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 52.2297, lng: 21.0122 });
  const [mapRef, setMapRef] = useState<any | null>(null);
  const [addressInput, setAddressInput] = useState<string>('');
  const [geocodingAddress, setGeocodingAddress] = useState<boolean>(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [editingMode, setEditingMode] = useState<boolean>(false);
  const [clickedLocation, setClickedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const wezelMarkersRef = useRef<any[]>([]);
  const budowaMarkersRef = useRef<any[]>([]);
  const selectedBudowaMarkerRef = useRef<any | null>(null);
  const setCoordinatesInFormRef = useRef<((lat: number, lng: number) => void) | null>(null);
  
  // Load regions
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const rid = params.get('region_id');
      const n = rid ? Number(rid) : NaN;
      if (Number.isFinite(n)) setSelectedRegion(n);
    } catch {}
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch('/api/auth/user-regions', { credentials: 'include' });
        if (res.ok) {
          const j = await res.json();
          const list = Array.isArray(j?.regions) ? j.regions : [];
          setRegions(list.map((r: any) => ({ id: Number(r.id), nazwa: String(r.nazwa || '') })));
          // Auto-select first region if none selected
          if (list.length > 0 && selectedRegion === null) {
            setSelectedRegion(list[0].id);
          }
        }
      } catch {}
    })();
  }, []);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (selectedRegion === null) {
        url.searchParams.delete('region_id');
      } else {
        url.searchParams.set('region_id', String(selectedRegion));
      }
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [selectedRegion]);

  // Load Google Maps API key and map ID from API if not already loaded (only after authentication)
  useAuthenticatedEffect(() => {
    // Check if we already have both from initial state
    if (apiKey && mapId && mapId.trim().length > 0) {
      return;
    }
    
    (async () => {
      try {
        // Load API key if missing
        if (!apiKey) {
          const keyResp = await authenticatedFetch('/api/geocode/key', { credentials: 'include' });
          if (keyResp.ok) {
            const keyData = await keyResp.json();
            if (keyData?.key && keyData.key.trim().length > 0) {
              const keyValue = keyData.key.trim();
              setApiKey(keyValue);
              try { (window as any).GMAPS_API_KEY = keyValue; } catch {}
              try { localStorage.setItem('GMAPS_API_KEY', keyValue); } catch {}
              try {
                const meta: HTMLMetaElement | null = (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null);
                if (!meta && typeof document !== 'undefined') {
                  const newMeta = document.createElement('meta');
                  newMeta.setAttribute('name', 'gmaps-api-key');
                  newMeta.setAttribute('content', keyValue);
                  document.head.appendChild(newMeta);
                } else if (meta) {
                  meta.setAttribute('content', keyValue);
                }
              } catch {}
            }
          }
        }
        
        // Load Map ID if missing
        if (!mapId || mapId.trim().length === 0) {
          const mapIdResp = await authenticatedFetch('/api/geocode/map-id', { credentials: 'include' });
          if (mapIdResp.ok) {
            const mapIdData = await mapIdResp.json();
            if (mapIdData?.mapId && mapIdData.mapId.trim().length > 0) {
              const mapIdValue = mapIdData.mapId.trim();
              setMapId(mapIdValue);
              try { (window as any).GMAPS_MAP_ID = mapIdValue; } catch {}
              try { localStorage.setItem('GMAPS_MAP_ID', mapIdValue); } catch {}
              try {
                const meta: HTMLMetaElement | null = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null);
                if (!meta && typeof document !== 'undefined') {
                  const newMeta = document.createElement('meta');
                  newMeta.setAttribute('name', 'gmaps-map-id');
                  newMeta.setAttribute('content', mapIdValue);
                  document.head.appendChild(newMeta);
                } else if (meta) {
                  meta.setAttribute('content', mapIdValue);
                }
              } catch {}
            } else {
              console.warn('[StandaloneBudowy] Map ID is empty or not provided from API');
            }
          } else {
            console.warn('[StandaloneBudowy] Failed to fetch Map ID from API:', mapIdResp.status, mapIdResp.statusText);
          }
        }
      } catch {}
    })();
  }, [apiKey, mapId]);
  
  // Watch for Map ID changes in meta tag or window (like MapView does)
  useEffect(() => {
    const current = readMapId();
    if (current && current.trim().length > 0 && current !== mapId) {
      setMapId(current.trim());
    }
  }, [mapId]);

  // Load wezels for selected region (only after authentication)
  useAuthenticatedEffect(() => {
    if (!selectedRegion || !apiKey) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedFetch(`/api/wezels?region_id=${selectedRegion}`, { credentials: 'include' });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        const wezelsList: Array<{ id: number; nazwa: string; latitude?: number | null; longitude?: number | null; lat?: number | null; lon?: number | null }> = data?.wezels || [];
        const validWezels = wezelsList
          .map(w => {
            const lat = Number(w.latitude ?? w.lat ?? NaN);
            const lng = Number(w.longitude ?? w.lon ?? NaN);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              return { id: w.id, nazwa: w.nazwa, lat, lng };
            }
            return null;
          })
          .filter((w): w is { id: number; nazwa: string; lat: number; lng: number } => w !== null);
        
        if (!cancelled) {
          setWezels(validWezels);
          // Center map on first wezel
          if (validWezels.length > 0) {
            setMapCenter({ lat: validWezels[0].lat, lng: validWezels[0].lng });
            if (mapRef) {
              try { mapRef.panTo({ lat: validWezels[0].lat, lng: validWezels[0].lng }); } catch {}
            }
          }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [selectedRegion, apiKey]);

  // Load budowy - only active ones with coordinates (only after authentication)
  useAuthenticatedEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedFetch('/api/budowa?activeOnly=true', { credentials: 'include' });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        const budowyList: Array<{ id: number; nazwa: string | null; latitude: number | null; longitude: number | null; aktywna: boolean }> = data?.budowy || [];
        const validBudowy = budowyList
          .filter(b => b.aktywna === true) // Only active budowy
          .map(b => {
            const lat = b.latitude !== null && b.latitude !== undefined ? Number(b.latitude) : NaN;
            const lng = b.longitude !== null && b.longitude !== undefined ? Number(b.longitude) : NaN;
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              return { id: b.id, nazwa: b.nazwa, lat, lng };
            }
            return null;
          })
          .filter((b): b is { id: number; nazwa: string | null; lat: number; lng: number } => b !== null);
        
        if (!cancelled) {
          setBudowy(validBudowy);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [apiKey]);

  // Center map on selected budowa when selected from list
  useEffect(() => {
    if (!selectedBudowaId || selectedBudowaId <= 0) return;
    
    // Clear temporary marker when selecting budowa from list
    if (clickedLocation) {
      setClickedLocation(null);
    }
    
    // Load full budowa details if not in budowy list (might be inactive)
    if (!budowy.find(b => b.id === selectedBudowaId) && mapRef) {
      (async () => {
        try {
          const resp = await authenticatedFetch(`/api/budowa/${selectedBudowaId}`, { credentials: 'include' });
          if (resp.ok) {
            const data = await resp.json();
            const b = data?.budowa;
            if (b && b.latitude !== null && b.longitude !== null) {
              const lat = Number(b.latitude);
              const lng = Number(b.longitude);
              if (Number.isFinite(lat) && Number.isFinite(lng)) {
                setMapCenter({ lat, lng });
                try {
                  if (mapRef) {
                    mapRef.panTo({ lat, lng });
                  }
                } catch {}
              }
            }
          }
        } catch {}
      })();
    } else if (mapRef) {
      const selectedBudowa = budowy.find(b => b.id === selectedBudowaId);
      if (selectedBudowa) {
        setMapCenter({ lat: selectedBudowa.lat, lng: selectedBudowa.lng });
        try {
          mapRef.panTo({ lat: selectedBudowa.lat, lng: selectedBudowa.lng });
        } catch {}
      }
    }
  }, [selectedBudowaId, budowy, mapRef, clickedLocation]);

  // Clear clicked location marker when editing mode ends (after save or cancel)
  useEffect(() => {
    if (!editingMode && clickedLocation) {
      setClickedLocation(null);
    }
  }, [editingMode]);

  // Geocoding handler
  const handleGeocode = async () => {
    if (!addressInput.trim() || geocodingAddress) return;
    setGeocodingAddress(true);
    setGeocodeError(null);
    try {
      const regionId = selectedRegion || 1;
      // Check for city names (more comprehensive)
      const hasCity = /(?:Warszawa|Kraków|Gdańsk|Wrocław|Poznań|Łódź|Katowice|Lublin|Bydgoszcz|Szczecin|Białystok|Rzeszów|Kielce|Olsztyn|Zielona\s*Góra|Opole|Gorzów|Toruń)/i.test(addressInput);
      
      // Use nearest wezel to current map center as reference point if no city in address
      // This helps Google Maps API bias results to the current map view (node location + 10km radius)
      let wezelIdParam = '';
      let radiusKmParam = '10'; // 10km radius as specified
      
      if (!hasCity && wezels.length > 0 && mapCenter) {
        // Find nearest wezel to current map center
        let nearestWezel = wezels[0];
        let minDist = Infinity;
        
        const haversineDist = (lat1: number, lng1: number, lat2: number, lng2: number) => {
          const R = 6371; // Earth radius in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLng = (lng2 - lng1) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                   Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                   Math.sin(dLng/2) * Math.sin(dLng/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return R * c;
        };
        
        for (const wezel of wezels) {
          const dist = haversineDist(mapCenter.lat, mapCenter.lng, wezel.lat, wezel.lng);
          if (dist < minDist) {
            minDist = dist;
            nearestWezel = wezel;
          }
        }
        
        // Use nearest wezel as reference point
        wezelIdParam = `&wezel_id=${nearestWezel.id}`;
        radiusKmParam = '10'; // 10km radius
      }
      
      let url = `/api/geocode/resolve?klient_budowa=${encodeURIComponent(addressInput.trim())}&region_id=${regionId}${wezelIdParam}&radius_km=${radiusKmParam}`;
      const resp = await authenticatedFetch(url, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success && data.lat && data.lon) {
          const lat = Number(data.lat);
          const lon = Number(data.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            setMapCenter({ lat, lng: lon });
            if (mapRef) {
              try { mapRef.panTo({ lat, lng: lon }); } catch {}
            }
            setGeocodeError(null);
            // Set coordinates in form if editing
            if (editingMode && setCoordinatesInFormRef.current) {
              setCoordinatesInFormRef.current(lat, lon);
            }
          } else {
            const errorMsg = data.message || data.error_message || 'Nie udało się znaleźć budowy';
            setGeocodeError(errorMsg);
          }
        } else {
          // Backend returned error details
          const errorMsg = data.message || data.error_message || 'Nie udało się znaleźć budowy';
          setGeocodeError(errorMsg);
        }
      } else {
        // HTTP error - try to get error message from response
        try {
          const errorData = await resp.json();
          const errorMsg = errorData.message || errorData.error_message || 'Nie udało się znaleźć budowy';
          setGeocodeError(errorMsg);
        } catch {
          setGeocodeError('Nie udało się znaleźć budowy (błąd sieci)');
        }
      }
    } catch (err) {
      console.error('[handleGeocode] error:', err);
      setGeocodeError('Nie udało się znaleźć budowy (błąd połączenia)');
    } finally {
      setGeocodingAddress(false);
    }
  };

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0, borderRight: panelOpen ? '1px solid var(--border)' : undefined }}>
        <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Address bar above map */}
          <div style={{ padding: '8px', background: 'var(--card)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10 }}>
            <input
              type="text"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!addressInput.trim() || geocodingAddress) return;
                  handleGeocode();
                }
              }}
              placeholder="Wpisz adres budowy lub kliknij na mapie..."
              style={{ flex: 1, padding: '6px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--card)', color: 'var(--text)' }}
            />
            <button
              onClick={handleGeocode}
              disabled={geocodingAddress || !addressInput.trim()}
              style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '4px', background: 'var(--accent)', color: 'white', border: 'none', cursor: geocodingAddress || !addressInput.trim() ? 'not-allowed' : 'pointer', opacity: geocodingAddress || !addressInput.trim() ? 0.5 : 1 }}
            >
              {geocodingAddress ? 'Szukam...' : 'Znajdź'}
            </button>
          </div>
          {geocodeError && (
            <div style={{ padding: '4px 8px', fontSize: '12px', color: 'var(--error)', background: 'var(--card)', borderBottom: '1px solid var(--border)' }}>
              {geocodeError}
            </div>
          )}
          
          {/* Region picker overlay */}
          <div style={{ position: 'absolute', top: geocodeError ? '74px' : '58px', left: 8, zIndex: 5, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text)' }}>Region:</span>
            <select value={selectedRegion ?? ''} onChange={(e) => setSelectedRegion(e.target.value ? Number(e.target.value) : null)} style={{ fontSize: 12, padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--card)', color: 'var(--text)' }}>
              <option value="">- wybierz -</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.nazwa}</option>
              ))}
            </select>
          </div>
          
          {/* Map container */}
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {apiKey ? (
              <BudowyMap
                apiKey={apiKey}
                mapId={mapId && mapId.trim() ? mapId.trim() : ''}
                center={mapCenter}
                wezels={wezels}
                budowy={budowy}
                selectedBudowaId={selectedBudowaId}
                clickedLocation={clickedLocation}
                onMapLoad={(map: any) => {
                  setMapRef(map);
                }}
                onWezelClick={(wezel) => {
                  setMapCenter({ lat: wezel.lat, lng: wezel.lng });
                  if (mapRef) {
                    try { mapRef.panTo({ lat: wezel.lat, lng: wezel.lng }); } catch {}
                  }
                }}
                onBudowaClick={(id) => {
                  setSelectedBudowaId(id);
                }}
                onMapClick={async (e: any) => {
                  try {
                    const lat = e.latLng?.lat();
                    const lng = e.latLng?.lng();
                    if (typeof lat === 'number' && typeof lng === 'number') {
                      // Set clicked location (this will add marker)
                      setClickedLocation({ lat, lng });
                      
                      // Reverse geocode to get address
                      try {
                        const resp = await authenticatedFetch(`/api/geocode/reverse?lat=${lat}&lon=${lng}`, { credentials: 'include' });
                        if (resp.ok) {
                          const data = await resp.json();
                          if (data.success && data.formatted_address) {
                            setAddressInput(data.formatted_address);
                          }
                        }
                      } catch (err) {
                        console.error('[onMapClick] reverse geocode error:', err);
                      }
                      
                      // Set coordinates in form if editing
                      if (editingMode && setCoordinatesInFormRef.current) {
                        setCoordinatesInFormRef.current(lat, lng);
                      }
                    }
                  } catch (err) {
                    console.error('[onMapClick] error:', err);
                  }
                }}
                editingMode={editingMode}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted text-sm">
                Ładowanie klucza Google Maps API...
              </div>
            )}
          </div>
        </div>
      </div>
      {panelOpen && (
        <div style={{ width: '720px', minWidth: 0 }}>
          <BudowyPanel 
            visible={true} 
            onClose={() => { setPanelOpen(false); }} 
            inline={true}
            selectedRegion={selectedRegion}
            selectedBudowaId={selectedBudowaId}
            onBudowaSelect={(id: number) => setSelectedBudowaId(id)}
            editingMode={editingMode}
            onEditingModeChange={setEditingMode}
            onGeocodeResult={(lat: number, lng: number) => {
              setMapCenter({ lat, lng });
              if (mapRef) {
                try { mapRef.panTo({ lat, lng }); } catch {}
              }
            }}
            onMapClickCoordinates={(lat: number, lng: number) => {
              // Coordinates are set via setCoordinatesInFormRef
            }}
            addressInput={addressInput}
            onAddressChange={setAddressInput}
            setCoordinatesInForm={setCoordinatesInFormRef}
            onBudowaSaved={() => {
              // Reload budowy list after save to show new marker
              (async () => {
                try {
                  const resp = await authenticatedFetch('/api/budowa?activeOnly=true', { credentials: 'include' });
                  if (resp.ok) {
                    const data = await resp.json();
                    const budowyList: Array<{ id: number; nazwa: string | null; latitude: number | null; longitude: number | null; aktywna: boolean }> = data?.budowy || [];
                    const validBudowy = budowyList
                      .filter(b => b.aktywna === true)
                      .map(b => {
                        const lat = b.latitude !== null && b.latitude !== undefined ? Number(b.latitude) : NaN;
                        const lng = b.longitude !== null && b.longitude !== undefined ? Number(b.longitude) : NaN;
                        if (Number.isFinite(lat) && Number.isFinite(lng)) {
                          return { id: b.id, nazwa: b.nazwa, lat, lng };
                        }
                        return null;
                      })
                      .filter((b): b is { id: number; nazwa: string | null; lat: number; lng: number } => b !== null);
                    setBudowy(validBudowy);
                  }
                } catch {}
              })();
              // Clear temporary marker after save
              setClickedLocation(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

const router = createBrowserRouter([
  { path: '/', element: <ErrorBoundaryDebug name="HomePage"><HomePage /></ErrorBoundaryDebug> },
  { path: '/budowy', element: <ErrorBoundaryDebug name="StandaloneBudowy"><StandaloneBudowy /></ErrorBoundaryDebug> },
  { path: '/admin', element: <Suspense fallback={null}><ErrorBoundaryDebug name="AdminPanel"><AdminPanel /></ErrorBoundaryDebug></Suspense> },
  { path: '/admin-nowy', element: <Navigate to="/admin" replace /> },
  { path: '/admin-stary', element: <Suspense fallback={null}><ErrorBoundaryDebug name="AdminDashboard"><AdminDashboard /></ErrorBoundaryDebug></Suspense> },
  { path: '/surowce', element: <Suspense fallback={null}><ErrorBoundaryDebug name="SurowcePage"><SurowcePage /></ErrorBoundaryDebug></Suspense> },
  { path: '/statistics', element: <Suspense fallback={null}><ErrorBoundaryDebug name="StatisticsPage"><StatisticsPage /></ErrorBoundaryDebug></Suspense> },
  { path: '*', element: <Navigate to="/" replace /> }
], { basename, future: { v7_relativeSplatPath: true } });

const VersionPill: React.FC = () => {
  const [versions, setVersions] = useState<{ deploy_client?: string | null; backend?: string | null; frontend?: string | null } | null>(null);
  const appVersion = (typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'dev') as string;
  const buildTime = (typeof BUILD_TIME !== 'undefined' ? BUILD_TIME : new Date().toISOString()) as string;

  useEffect(() => {
    const ctrl = new AbortController();
    authenticatedFetch('/api/debug/versions', { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j && j.success) setVersions(j.versions || {}); })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  return (
    <div style={{ position: 'fixed', bottom: 8, right: 8, background: 'rgba(33,33,33,0.85)', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: 11 }}>
      {`FE:${appVersion}`} {buildTime ? `| ${buildTime}` : ''}
      {versions ? ` | DEPLOY:${versions.deploy_client || '-'} | BE:${versions.backend || '-'}` : ''}
    </div>
  );
};

const AppShell: React.FC = () => {
  const { status, isInitialLoad } = useConnection();
  return (
    <ErrorBoundaryDebug name="AppShell">
      <div className="App">
        {/* Show offline banner only if offline AND not during initial load */}
        {status === 'offline' && !isInitialLoad && <OfflineBanner />}
        <RouterProvider router={router} />
        <VersionPill />
        {/* Show connecting overlay during initial load or when connecting */}
        {(status === 'connecting' || (status === 'offline' && isInitialLoad)) && (
          <ConnectingOverlay message={isInitialLoad ? "Ładowanie strony…" : "Łączenie z serwerem…"} />
        )}
      </div>
    </ErrorBoundaryDebug>
  );
};

const App: React.FC = () => {
  console.log('[App] Renderowanie głównego komponentu App');
  
  try {
    return (
      <AuthProvider>
        <ThemeProvider>
          <DateProvider>
            <StatusColorsProvider>
              <ConnectionProvider>
                <NotificationProvider module="zamowienia">
                  <ChatProvider>
                    <AppShell />
                  </ChatProvider>
                </NotificationProvider>
              </ConnectionProvider>
            </StatusColorsProvider>
          </DateProvider>
        </ThemeProvider>
      </AuthProvider>
    );
  } catch (error: any) {
    console.error('[App] BŁĄD podczas renderowania:', {
      message: error?.message,
      stack: error?.stack,
      name: error?.name
    });
    throw error;
  }
};

export default App;

 